import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { CircularProgress } from '@mui/material'
import ImageViewer from 'components/ImageViewer'
import { searchLocalFiles } from 'services/fileService'
import { attachReferenceFiles, detachReferenceFile } from 'services/taskService'

const Section = styled.div`
  margin-top: 16px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const Title = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundColor};
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Item = styled.div`
  display: flex;
  align-items: ${({ $top }) => ($top ? 'flex-start' : 'center')};
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
`

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const ItemName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ItemNameButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

const ItemMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const ItemDescription = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};
`

const PhotoThumb = styled.img`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  background: ${({ theme }) => theme.colors.backgroundColor};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`

const ItemLabel = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: #6d28d9;
  background: #ede9fe;
  border-radius: 6px;
  padding: 2px 6px;
  display: inline-block;
  align-self: flex-start;
`

const FileGlyph = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
`

const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[200]};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundColor};
    color: ${({ theme }) => theme.colors.error};
  }
`

const Empty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-style: italic;
`

const PickerWrap = styled.div`
  margin-top: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  background: #fff;
  padding: 10px;
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  border-radius: 6px;
  padding: 6px 10px;
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const Results = styled.div`
  max-height: 220px;
  overflow-y: auto;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 13px;
  min-height: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundColor};
    border-color: ${({ theme }) => theme.colors.lightGrey};
  }

  &:disabled { opacity: 0.6; cursor: progress; }
`

const ResultBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const ResultName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ResultMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 2px;
`

// Files come from two different sources — the search endpoint
// (LightweightFileResource → camelCase) and TaskResource.reference_files.
// Both should expose a human-friendly label; fall through the likely
// fields so we never render a blank row.
const fileLabel = (f) =>
  f?.displayName || f?.fileName || f?.name || f?.original_name || 'Untitled file'

const fileTypeLabel = (f) =>
  f?.fileType?.name || f?.fileTypeName || f?.category?.name || f?.categoryName || null

const uploaderLabel = (f) => {
  const u = f?.uploader
  if (!u) return f?.uploadedByEmail || null
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || u.email || null
}

const uploadedAtIso = (f) =>
  f?.uploadedAt || f?.createdAt || null

const formatRelativeDate = (iso) => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const diff = (Date.now() - date.getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatFileSize = (bytes) => {
  if (bytes == null || bytes <= 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const fileIconFor = (mime) => {
  const m = (mime || '').toLowerCase()
  if (m.startsWith('image/')) return '🖼'
  if (m.startsWith('video/')) return '🎬'
  if (m.includes('pdf')) return '📄'
  if (m.includes('word') || m.includes('docx') || m.includes('doc')) return '📝'
  if (m.includes('excel') || m.includes('sheet') || m.includes('xlsx')) return '📊'
  return null
}

const TaskReferenceFiles = ({ task, canManage, onReload }) => {
  const [picking, setPicking] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [busyFileId, setBusyFileId] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [thumbFailures, setThumbFailures] = useState({})
  const abortRef = useRef(null)

  const refs = useMemo(() => task?.referenceFiles || [], [task?.referenceFiles])
  const attachedIds = useMemo(() => new Set(refs.map(r => r.id)), [refs])
  const bpId = task?.building?.id

  useEffect(() => {
    if (!picking || !bpId) return
    const trimmed = query.trim()
    if (!trimmed) {
      setResults([])
      return
    }

    // 250ms debounce — the API is fast but each keystroke hammering it
    // is unfriendly and the abort-and-retry pattern below still flashes
    // the spinner. Bail the timer if the user keeps typing.
    const timer = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      setSearching(true)
      searchLocalFiles({ bpId, query: trimmed, searchMode: 'name' }, ctrl.signal)
        .then(res => {
          const list = res?.data?.data || res?.data?.files || res?.data || []
          const items = Array.isArray(list) ? list : (list.items || [])
          setResults(items.filter(f => !attachedIds.has(f.id)).slice(0, 20))
        })
        .catch(err => {
          if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
            setResults([])
          }
        })
        .finally(() => setSearching(false))
    }, 250)

    return () => clearTimeout(timer)
  }, [picking, bpId, query, attachedIds])

  const handleAttach = useCallback(async (fileId) => {
    setBusyFileId(fileId)
    try {
      await attachReferenceFiles({ taskId: task.id, fileIds: [fileId] })
      setQuery('')
      setResults([])
      if (onReload) await onReload()
    } finally {
      setBusyFileId(null)
    }
  }, [task?.id, onReload])

  const handleDetach = useCallback(async (fileId) => {
    setBusyFileId(fileId)
    try {
      await detachReferenceFile({ taskId: task.id, fileId })
      if (onReload) await onReload()
    } finally {
      setBusyFileId(null)
    }
  }, [task?.id, onReload])

  return (
    <Section>
      <Header>
        <Title>
          <MenuBookOutlinedIcon style={{ fontSize: 16 }} />
          Reference documents
        </Title>
        {canManage && !picking && (
          <AddBtn onClick={() => setPicking(true)}>
            <AddIcon style={{ fontSize: 14 }} /> Attach
          </AddBtn>
        )}
      </Header>

      {refs.length === 0 && !picking && (
        <Empty>No reference SOPs or guides linked yet.</Empty>
      )}

      {refs.length > 0 && (
        <List>
          {refs.map(ref => {
            const label = fileLabel(ref)
            const type = fileTypeLabel(ref)
            const uploader = uploaderLabel(ref)
            const when = formatRelativeDate(uploadedAtIso(ref))
            const size = formatFileSize(ref.size)
            const emoji = fileIconFor(ref.mimeType)
            const metaBits = [type, uploader, when, size].filter(Boolean)
            const photoUrl = ref.isPhoto ? (ref.viewUrl || ref.thumbnailUrl) : null
            const thumb = ref.isPhoto ? [ref.thumbnailUrl, ref.viewUrl].filter(Boolean)[thumbFailures[ref.id] || 0] : null
            const description = ref.isPhoto ? ref.description : null
            const openPhoto = photoUrl ? () => setViewing({ id: ref.id, url: photoUrl, name: label }) : null
            return (
              <Item key={ref.id} $top={!!thumb || !!description}>
                {thumb ? (
                  <PhotoThumb
                    src={thumb}
                    alt={label}
                    role='button'
                    tabIndex={0}
                    aria-label={`View the photo ${label}`}
                    onClick={openPhoto}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openPhoto()
                      }
                    }}
                    onError={() => setThumbFailures(prev => ({ ...prev, [ref.id]: (prev[ref.id] || 0) + 1 }))}
                  />
                ) : (
                  <FileGlyph aria-hidden>
                    {emoji || <InsertDriveFileOutlinedIcon style={{ fontSize: 18, color: '#64748b' }} />}
                  </FileGlyph>
                )}
                <ItemBody>
                  {openPhoto ? (
                    <ItemNameButton type='button' title={label} onClick={openPhoto}>{label}</ItemNameButton>
                  ) : (
                    <ItemName title={label}>{label}</ItemName>
                  )}
                  {metaBits.length > 0 && (
                    <ItemMeta>{metaBits.join(' · ')}</ItemMeta>
                  )}
                  {description && (
                    <ItemDescription>{description}</ItemDescription>
                  )}
                  {ref.label && (
                    <ItemLabel>{ref.label}</ItemLabel>
                  )}
                </ItemBody>
                {canManage && (
                  <RemoveBtn
                    onClick={() => handleDetach(ref.id)}
                    disabled={busyFileId === ref.id}
                    title="Remove reference"
                  >
                    {busyFileId === ref.id
                      ? <CircularProgress size={12} />
                      : <CloseIcon style={{ fontSize: 14 }} />}
                  </RemoveBtn>
                )}
              </Item>
            )
          })}
        </List>
      )}

      {picking && (
        <PickerWrap>
          <SearchRow>
            <SearchIcon style={{ fontSize: 16, color: '#64748b' }} />
            <SearchInput
              autoFocus
              placeholder="Search Building Passport files by name…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <RemoveBtn onClick={() => { setPicking(false); setQuery(''); setResults([]) }} title="Close">
              <CloseIcon style={{ fontSize: 14 }} />
            </RemoveBtn>
          </SearchRow>
          <Results>
            {searching && <Empty>Searching…</Empty>}
            {!searching && query.trim() && results.length === 0 && (
              <Empty>No matching files.</Empty>
            )}
            {!searching && results.map(file => {
              const label = fileLabel(file)
              const type = fileTypeLabel(file)
              const uploader = uploaderLabel(file)
              const when = formatRelativeDate(uploadedAtIso(file))
              const size = formatFileSize(file.size)
              const emoji = fileIconFor(file.mimeType)
              const metaBits = [type, uploader, when, size].filter(Boolean)
              return (
                <ResultItem
                  key={file.id}
                  onClick={() => handleAttach(file.id)}
                  disabled={busyFileId === file.id}
                >
                  <FileGlyph aria-hidden>
                    {emoji || <InsertDriveFileOutlinedIcon style={{ fontSize: 16, color: '#64748b' }} />}
                  </FileGlyph>
                  <ResultBody>
                    <ResultName title={label}>{label}</ResultName>
                    {metaBits.length > 0 && <ResultMeta>{metaBits.join(' · ')}</ResultMeta>}
                  </ResultBody>
                  {busyFileId === file.id && <CircularProgress size={12} />}
                </ResultItem>
              )
            })}
            {!searching && !query.trim() && (
              <Empty>Type a few letters of the file name to search.</Empty>
            )}
          </Results>
        </PickerWrap>
      )}

      {viewing && (
        <ImageViewer
          open
          onClose={() => setViewing(null)}
          file={{ id: viewing.id, url: viewing.url, name: viewing.name, displayName: viewing.name }}
        />
      )}
    </Section>
  )
}

export default TaskReferenceFiles
